import { verifyOAuthTokenApi } from "@/api/services/main/auth";
import { AuthLoadingScreen } from "@/features/auth/components/AuthLoadingScreen";
import { REDIRECT_URL_QUERY_KEY } from "@/features/auth/constant/login";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { logout } from "@/utils/logout";
import { getSafeRedirectURL } from "@/utils/url";
import { FC, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Generic OAuth handoff callback.
 *
 * A single-use `token` is minted server-side (currently by admin impersonation) and stashed in Valkey.
 * This page exchanges it for the target user's session via `verifyOAuthTokenApi`, which persists the
 * tokens, then redirects into the app. Kept feature-agnostic so future SSO/handoff flows can reuse it.
 */
export const AuthCallbackPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isProcessing = useRef(false);

  const token = searchParams.get("token");
  const redirectUrl = searchParams.get(REDIRECT_URL_QUERY_KEY);

  useEffect(() => {
    if (!token) {
      navigate(AppRoutes.login, { replace: true });
      return;
    }

    // Prevent double execution in StrictMode (the token is single-use).
    if (isProcessing.current) return;
    isProcessing.current = true;

    const exchangeToken = async () => {
      try {
        await logout();

        await verifyOAuthTokenApi({ token });

        const target = getSafeRedirectURL(redirectUrl);
        if (target.startsWith("/")) {
          navigate(target, { replace: true });
        } else {
          globalThis.location.replace(target);
        }
      } catch (error) {
        showError(error);
        navigate(AppRoutes.login, { replace: true });
      }
    };

    exchangeToken();
  }, [token, redirectUrl, navigate]);

  return <AuthLoadingScreen />;
};
